// AI 知识助手客户端：统一读取配置、构建提示词、外部调用与本地兜底
import { buildSelectivityMatrix } from './tccUtils';

const STORAGE_KEY = 'aiOptions';

export const getAiOptions = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const opts = raw ? JSON.parse(raw) : {};
    return {
      endpointUrl: opts.endpointUrl || '',
      apiKey: opts.apiKey || '',
      model: opts.model || 'gpt-4o-mini',
      temperature: typeof opts.temperature === 'number' ? opts.temperature : 0.2,
      maxTokens: typeof opts.maxTokens === 'number' ? opts.maxTokens : 800,
    };
  } catch (_) {
    return { endpointUrl: '', apiKey: '', model: 'gpt-4o-mini', temperature: 0.2, maxTokens: 800 };
  }
};

export const saveAiOptions = (opts = {}) => {
  const payload = {
    endpointUrl: String(opts.endpointUrl || '').trim(),
    apiKey: String(opts.apiKey || '').trim(),
    model: String(opts.model || 'gpt-4o-mini').trim(),
    temperature: typeof opts.temperature === 'number' ? opts.temperature : 0.2,
    maxTokens: typeof opts.maxTokens === 'number' ? opts.maxTokens : 800,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  return payload;
};

export const isConfigured = (opts = null) => {
  const o = opts || getAiOptions();
  return !!(o.endpointUrl && o.apiKey && o.model);
};

// 构建曲线分析提示词（中文）
export const buildCurvePrompt = (series = [], axesType = 'I-t', { locale = 'zh' } = {}) => {
  const lines = [];
  lines.push('你是一名继电保护工程领域的资深助手，基于反时限曲线与选择性原则给出简洁、具操作性的分析。');
  lines.push('请按如下结构输出：');
  lines.push('1) 动作时间趋势（每条曲线简述）；');
  lines.push('2) 潜在非选择性（指出最可能问题点与电流范围）；');
  lines.push('3) 微调建议（仅给出TMS或拾取电流的具体调整建议）。');
  lines.push('');
  lines.push(`坐标系: ${axesType}`);
  (series || []).forEach((s, i) => {
    const meta = s.meta || {};
    const label = s.label || `曲线${i + 1}`;
    const pickup = s.pickup;
    const TMS = s.TMS;
    const curveType = s.curveType || meta.curveType || 'NI';
    lines.push(`- ${label}: curve=${curveType}, pickup=${pickup}, TMS=${TMS}`);
  });
  if (axesType === 'I-t') {
    const matrix = buildSelectivityMatrix(series, { minMargin: 0.3, sequentialAdjust: true });
    lines.push('');
    lines.push(`链路校核结果: overallOk=${matrix.overallOk ? '是' : '否'}, pairs=${matrix.pairs.length}`);
    matrix.pairs.forEach(p => {
      const rec = p.recommendation && typeof p.recommendation.recommended === 'number' ? `→ 建议上级TMS≈${p.recommendation.recommended}${p.recommendation.capped ? '（受限）' : ''}` : '';
      lines.push(`  · ${p.downstream} → ${p.upstream}: worstMargin=${p.worstMargin ?? '—'}s @ I=${p.worstAtI ?? '—'}; ok=${p.ok ? '是' : '否'} ${rec}`);
    });
  }
  lines.push('请避免冗长理论，直接给出现场可执行的建议。');
  return lines.join('\n');
};

// 外部模型调用（OpenAI 兼容接口），失败时抛错
export const callExternalAi = async (prompt, opts = null) => {
  const o = opts || getAiOptions();
  const url = o.endpointUrl;
  if (!url) throw new Error('AI endpoint 未配置');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${o.apiKey}`,
  };
  const body = {
    model: o.model,
    messages: [
      { role: 'system', content: '你是一名继电保护工程助手，输出中文。' },
      { role: 'user', content: prompt }
    ],
    temperature: o.temperature,
    max_tokens: o.maxTokens,
  };
  const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`AI 请求失败: ${res.status} ${txt}`);
  }
  const data = await res.json();
  const text = data?.choices?.[0]?.message?.content || data?.choices?.[0]?.text || '';
  if (!text) throw new Error('AI 响应为空');
  return text;
};

// 本地兜底：基于选择性矩阵与曲线族常识生成简述
export const generateInsightLocal = (series = [], axesType = 'I-t') => {
  const lines = [];
  lines.push('动作时间趋势:');
  (series || []).forEach((s, i) => {
    const label = s.label || `曲线${i + 1}`;
    const ct = (s.curveType || '').toUpperCase();
    const trend = ct.includes('EI') ? '极反时限，随电流倍数上升动作时间急剧下降' : ct.includes('VI') ? '很反时限，动作时间显著下降' : '普通反时限，动作时间随电流上升逐步下降';
    lines.push(`- ${label}: ${trend}；pickup≈${s.pickup}A，TMS≈${s.TMS}`);
  });

  if (axesType === 'I-t') {
    const matrix = buildSelectivityMatrix(series, { minMargin: 0.3, sequentialAdjust: true });
    const issues = matrix.pairs.filter(p => !p.ok);
    lines.push('潜在非选择性:');
    if (issues.length === 0) {
      lines.push('- 未发现明显非选择性，当前相邻链路满足最小裕度≈0.3s');
    } else {
      issues.forEach(p => {
        lines.push(`- ${p.downstream} 与 ${p.upstream}: 最差裕度≈${p.worstMargin ?? '—'}s，发生在 I≈${p.worstAtI ?? '—'}A`);
      });
    }
    lines.push('微调建议:');
    if (matrix.adjustments.length === 0) {
      lines.push('- 保持下级参数不变，上级可适度增大 TMS（例如 +0.05~+0.10）以拉开裕度');
    } else {
      matrix.adjustments.forEach(adj => {
        lines.push(`- 将上级【${adj.label}】TMS: ${adj.from} → ${adj.to}${adj.capped ? '（受限上限）' : ''}，目标最小裕度≈${adj.minMargin}s`);
      });
    }
  } else {
    lines.push('潜在非选择性与微调建议: 当前坐标系非 I-t，建议在 I-t 叠加视图进行选择性校核以获得具体 TMS 建议。');
  }

  return lines.join('\n');
};

// 统一入口：优先外部模型，失败或未配置则使用本地兜底
export const askCurves = async (series = [], axesType = 'I-t', { locale = 'zh' } = {}) => {
  const prompt = buildCurvePrompt(series, axesType, { locale });
  const opts = getAiOptions();
  try {
    if (isConfigured(opts)) {
      const text = await callExternalAi(prompt, opts);
      return { source: 'external', text, prompt };
    }
  } catch (err) {
    // 外部调用失败，转兜底
    console.warn('AI 外部调用失败，降级到本地兜底:', err);
  }
  const text = generateInsightLocal(series, axesType);
  return { source: 'local', text, prompt };
};

export default {
  getAiOptions,
  saveAiOptions,
  isConfigured,
  buildCurvePrompt,
  callExternalAi,
  generateInsightLocal,
  askCurves,
};